import {
	ApartmentOutlined,
	BankOutlined,
	BarChartOutlined,
	BookOutlined,
	CalendarOutlined,
	CloseCircleOutlined,
	DashboardOutlined,
	DeleteOutlined,
	DollarOutlined,
	FileProtectOutlined,
	HomeOutlined,
	IdcardOutlined,
	LaptopOutlined,
	ReadOutlined,
	ScanOutlined,
	ScheduleOutlined,
	SettingOutlined,
	TeamOutlined,
	UsergroupAddOutlined,
	UserOutlined,
	VideoCameraOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import { Badge, Layout, Menu } from "antd";
import type { ItemType } from "antd/es/menu/interface";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useStudents } from "@/modules/students/queries/student.query";
import { useOrganizationStore } from "@/shared/store/organization.store";

const { Sider } = Layout;

const SYNTHETIC_ROUTES: Record<string, string> = {};

/**
 * Sidebar doim `theme="dark"`, antd Badge esa bolasiga global `colorText` beradi —
 * light rejimda bu qora bo'lib, qora fonda matn ko'rinmay qoladi. Menu item rangini
 * meros qilib olish shu holatni tuzatadi.
 */
const badgeStyles = { root: { color: "inherit" } } as const;

function getOpenKeys(pathname: string): string[] {
	if (
		pathname.startsWith("/educational/academic-years") ||
		pathname.startsWith("/educational/academic-terms") ||
		pathname.startsWith("/educational/holidays") ||
		pathname.startsWith("/settings/subjects") ||
		pathname.startsWith("/educational/classrooms")
	) {
		return ["qoshimcha-sozlamalar"];
	}
	return [];
}

function getMainItems(collapsed: boolean): ItemType {
	return {
		type: "group",
		label: collapsed ? null : "ASOSIY SAHIFA",
		children: [
			{ key: "/dashboard", icon: <DashboardOutlined />, label: "Dashboard" },
			{ key: "/attendance/students", icon: <CalendarOutlined />, label: "Davomat" },
			{ key: "/educational/schedule", icon: <ScheduleOutlined />, label: "Dars jadvali" },
		],
	};
}

function getSchoolItems(
	collapsed: boolean,
	pendingCount: number,
	unassignedCount: number
): ItemType[] {
	return [
		{
			type: "group",
			label: collapsed ? null : "MAKTAB",
			children: [
				{ key: "/classes", icon: <TeamOutlined />, label: "Sinflar" },
				{
					key: "/students",
					icon: <UsergroupAddOutlined />,
					label: (
						<Badge count={pendingCount} size="small" offset={[6, 0]} styles={badgeStyles}>
							O'quvchilar
						</Badge>
					),
				},
				{
					key: "/students/pending",
					icon: <UsergroupAddOutlined />,
					label: "Tasdiqlash kutmoqda",
				},
				{
					key: "/students/unassigned",
					icon: <TeamOutlined />,
					label: (
						<Badge count={unassignedCount} size="small" offset={[6, 0]} styles={badgeStyles}>
							Sinfsiz o'quvchilar
						</Badge>
					),
				},
				{ key: "/educational/teachers", icon: <UserOutlined />, label: "O'qituvchilar" },
				{ key: "/guardians", icon: <TeamOutlined />, label: "Ota-onalar" },
			],
		},
		{
			type: "group",
			label: collapsed ? null : "HISOBOTLAR",
			children: [
				{
					key: "/attendance/absent-students",
					icon: <CloseCircleOutlined />,
					label: "Kelmagan o'quvchilar",
				},
				{
					key: "/attendance/excused-students",
					icon: <FileProtectOutlined />,
					label: "Sababli kelmaydigan o'quvchilar",
				},
				{
					key: "/attendance/chronic-absentees",
					icon: <WarningOutlined />,
					label: "Muntazam qo'ldiruvchilar",
				},
			],
		},
		{
			type: "group",
			label: collapsed ? null : "BOSHQARUV",
			children: [
				{
					key: "/school-setup",
					icon: <SettingOutlined />,
					label: "Maktab sozlamalari",
				},
				{
					key: "/attendance/excuses",
					icon: <FileProtectOutlined />,
					label: "Darsdan ozod qilish",
				},
				{
					key: "qoshimcha-sozlamalar",
					icon: <CalendarOutlined />,
					label: "Qo'shimcha sozlamalar",
					children: [
						{ key: "/educational/academic-years", label: "O'quv yillari" },
						{ key: "/educational/academic-terms", label: "Choraklar" },
						{ key: "/educational/holidays", label: "Tatil kunlari" },
						{ key: "/settings/subjects", label: "Fanlar" },
						{ key: "/educational/classrooms", label: "Dars xonalari" },
					],
				},
			],
		},
		{
			type: "group",
			label: collapsed ? null : "INFRATUZILMA",
			children: [
				{ key: "/attendance/nvr-cameras", icon: <VideoCameraOutlined />, label: "Kameralar" },
				{ key: "/attendance/face-enrollment", icon: <ScanOutlined />, label: "Yuz ro'yxati" },
			],
		},
	];
}

function getSupervisionItems(collapsed: boolean): ItemType {
	return {
		type: "group",
		label: collapsed ? null : "NAZORAT",
		children: [
			{
				key: "/attendance/school-cameras",
				icon: <VideoCameraOutlined />,
				label: "Maktab kameralari",
			},
		],
	};
}

function getDistrictItems(collapsed: boolean): ItemType {
	return {
		type: "group",
		label: collapsed ? null : "TUMAN",
		children: [
			{ key: "/organizations/schools", icon: <BankOutlined />, label: "Maktablar" },
			{ key: "/reports", icon: <BarChartOutlined />, label: "Hisobotlar" },
			{ key: "/organizations/children", icon: <ApartmentOutlined />, label: "Quyi tashkilotlar" },
			{ key: "/organizations/users", icon: <UserOutlined />, label: "Foydalanuvchilar" },
		],
	};
}

function getAdminItems(collapsed: boolean): ItemType {
	return {
		type: "group",
		label: collapsed ? null : "ADMIN",
		children: [
			{ key: "/users", icon: <UserOutlined />, label: "Foydalanuvchilar" },
			{ key: "/organization-types", icon: <ApartmentOutlined />, label: "Tashkilot turlari" },
			{ key: "/positions", icon: <IdcardOutlined />, label: "Lavozimlar" },
			{ key: "/organizations/children", icon: <ApartmentOutlined />, label: "Quyi tashkilotlar" },
			{ key: "/cross-access", icon: <ApartmentOutlined />, label: "Boshqa tashkilotlar" },
			{ key: "/organizations/users", icon: <UserOutlined />, label: "Org foydalanuvchilar" },
			{ key: "/organizations/devices", icon: <LaptopOutlined />, label: "Qurilmalar" },
			{
				key: "/organizations/face-enrollment",
				icon: <ScanOutlined />,
				label: "Yuz ro'yxatga olish",
			},
			{
				key: "sozlamalar",
				icon: <SettingOutlined />,
				label: "Sozlamalar",
				children: [
					{ key: "/settings/staff", icon: <TeamOutlined />, label: "Xodimlar sozlamalari" },
					{ key: "/settings/shifts", icon: <CalendarOutlined />, label: "Smena sozlamalari" },
					{ key: "/settings/academic-years", icon: <BookOutlined />, label: "O'quv yillari" },
					{ key: "/settings/weekdays", icon: <CalendarOutlined />, label: "Ish kunlari" },
					{ key: "/settings/subjects", icon: <ReadOutlined />, label: "Fanlar" },
					{ key: "/settings/room-types", icon: <HomeOutlined />, label: "Xona turlari" },
					{ key: "/settings/tariffs", icon: <DollarOutlined />, label: "Tariflar" },
					{ key: "/settings/payments", icon: <DollarOutlined />, label: "To'lovlar" },
					{ key: "/settings/academic-terms", icon: <CalendarOutlined />, label: "Choraklar" },
					{ key: "/settings/holidays", icon: <CalendarOutlined />, label: "Tatil kunlari" },
				],
			},
			{ key: "/trash", icon: <DeleteOutlined />, label: "Axlat qutisi" },
		],
	};
}

interface SidebarMenuProps {
	collapsed: boolean;
}

export function SidebarMenu({ collapsed }: SidebarMenuProps) {
	const navigate = useNavigate();
	const location = useLocation();

	const isMbos = useOrganizationStore((s) =>
		s.organizations.some((org) => org.name === "Mbos" && org.parentId === null)
	);
	const isEducational = useOrganizationStore((s) => {
		const selectedId = s.selectedOrgId;
		return s.organizations.find((o) => o.id === selectedId)?.isEducational ?? false;
	});
	const gradeOrder = useOrganizationStore((s) => {
		const selectedId = s.selectedOrgId;
		return s.organizations.find((o) => o.id === selectedId)?.grade?.order ?? 0;
	});
	const selectedOrgId = useOrganizationStore((s) => s.selectedOrgId);

	// Tuman/shahar darajasi: grade 30-39, ta'lim emas
	const isDistrict = !isEducational && gradeOrder >= 30 && gradeOrder < 40;

	// Pending students count — only fetch for educational orgs
	const { data: pendingData } = useStudents({
		organizationId: isEducational ? (selectedOrgId ?? "") : "",
		page: 1,
		limit: 1,
		status: "pending",
	});
	const pendingCount = isEducational ? (pendingData?.data?.meta?.total ?? 0) : 0;

	// Sinfsiz o'quvchilar soni — faqat ta'lim tashkilotlari uchun so'raladi
	const { data: unassignedData } = useStudents({
		organizationId: isEducational ? (selectedOrgId ?? "") : "",
		page: 1,
		limit: 1,
		status: "all",
		assignment: "unassigned",
	});
	const unassignedCount = isEducational ? (unassignedData?.data?.meta?.total ?? 0) : 0;

	const menuItems = useMemo<ItemType[]>(() => {
		const items: ItemType[] = [getMainItems(collapsed)];
		if (isEducational) {
			items.push(...getSchoolItems(collapsed, pendingCount, unassignedCount));
		} else {
			// Yuqori (nazorat qiluvchi) tashkilotlar: viloyat/tuman/respublika/Mbos —
			// avlod maktablar kameralarini daraxt orqali jonli ko'rish.
			items.push(getSupervisionItems(collapsed));
		}
		if (isDistrict) {
			items.push(getDistrictItems(collapsed));
		}
		if (isMbos) {
			items.push(getAdminItems(collapsed));
		}
		return items;
	}, [isMbos, isEducational, isDistrict, collapsed, pendingCount, unassignedCount]);

	return (
		<Sider
			trigger={null}
			collapsible
			collapsed={collapsed}
			theme="dark"
			width={264}
			style={{
				overflow: "auto",
				height: "100vh",
				position: "fixed",
				left: 0,
				top: 0,
				bottom: 0,
				zIndex: 11,
				background: "#111827",
				borderRight: "1px solid rgba(255,255,255,0.06)",
			}}
		>
			<div
				className="flex items-center"
				style={{
					height: 64,
					padding: collapsed ? "0" : "0 20px",
					justifyContent: collapsed ? "center" : "flex-start",
				}}
			>
				{collapsed ? (
					<img src="/logoM.png" alt="LogoM" className="h-8 w-8" />
				) : (
					<img src="/logo.png" alt="Logo" style={{ height: 32 }} />
				)}
			</div>

			<div
				style={{
					height: 1,
					background: "rgba(255,255,255,0.08)",
					margin: "0 16px 8px",
				}}
			/>

			<Menu
				theme="dark"
				mode="inline"
				selectedKeys={[location.pathname]}
				defaultOpenKeys={getOpenKeys(location.pathname)}
				items={menuItems}
				className="sidebar-menu"
				onClick={({ key }) => navigate(SYNTHETIC_ROUTES[key] ?? key)}
				style={{ background: "transparent", border: "none" }}
			/>
		</Sider>
	);
}
